import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Users2, Calendar, LogOut, Shield, CalendarDays, UserCircle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import CatMascot from "../components/CatMascot";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Skeleton } from "../components/ui/skeleton";

const CATEGORY_META = {
  normal: { label: "Normal", icon: Calendar, color: "#8B5CF6" },
  competition: { label: "Competition", icon: Trophy, color: "#F59E0B" },
  teamwork: { label: "Teamwork", icon: Users2, color: "#10B981" },
};

export default function MemberPortal() {
  const { user, logout } = useAuth();
  const [events, setEvents] = useState(null);
  const [staff, setStaff] = useState(null);

  useEffect(() => {
    api.get("/member/events").then((r) => setEvents(r.data));
    api.get("/member/staff").then((r) => setStaff(r.data));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0C10] grid-bg text-slate-100">
      <div className="pointer-events-none fixed -top-40 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-purple-700/15 blur-[120px]" />

      <header className="sticky top-0 z-40 border-b border-slate-800/80 bg-[#0C0F16]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <CatMascot size={38} />
            <div>
              <p className="font-display text-lg font-black leading-none text-white">GG's Kingdom</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-purple-400">Community Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 rounded-xl bg-slate-800/40 px-3 py-1.5">
              <Avatar className="h-7 w-7 border border-indigo-500/40"><AvatarImage src={user?.discord_avatar} /><AvatarFallback className="bg-indigo-600/30 text-[10px] text-indigo-200">{(user?.discord_username || "GG").slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
              <span className="hidden text-sm font-medium text-white sm:block">{user?.discord_username}</span>
            </div>
            <Button data-testid="member-logout-btn" onClick={logout} variant="ghost" size="sm" className="gap-1 text-slate-400 hover:bg-red-500/15 hover:text-red-400"><LogOut className="h-4 w-4" /> Sign out</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="font-display text-3xl font-black text-white">Welcome, {user?.discord_username}!</h1>
          <p className="mt-1 text-sm text-slate-400">Browse the Kingdom's events and meet the team. This is a view-only community portal.</p>
        </motion.div>

        <Tabs defaultValue="events">
          <TabsList className="bg-slate-900/60">
            <TabsTrigger value="events" data-testid="member-tab-events"><CalendarDays className="mr-2 h-4 w-4" /> Events</TabsTrigger>
            <TabsTrigger value="staff" data-testid="member-tab-staff"><UserCircle className="mr-2 h-4 w-4" /> Team</TabsTrigger>
          </TabsList>

          <TabsContent value="events" className="mt-6">
            {!events ? (
              <div className="grid gap-4 sm:grid-cols-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl bg-slate-800/50" />)}</div>
            ) : events.length === 0 ? (
              <div className="card-glass flex flex-col items-center rounded-2xl py-16 text-center"><Calendar className="h-10 w-10 text-slate-600" /><p className="mt-3 text-sm text-slate-400">No events yet. Check back soon!</p></div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {events.map((e, i) => {
                  const meta = CATEGORY_META[e.category] || CATEGORY_META.normal;
                  return (
                    <motion.div key={e.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="card-glass rounded-2xl p-5" data-testid={`member-event-${e.id}`}>
                      <div className="flex items-start gap-3">
                        <span className="rounded-lg p-2" style={{ background: `${meta.color}22`, color: meta.color }}><meta.icon className="h-5 w-5" /></span>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-white">{e.title}</p>
                          <p className="text-xs text-slate-400">{new Date(e.scheduled_at).toLocaleString()}</p>
                          {e.description && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{e.description}</p>}
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                        <span className="rounded-full px-2.5 py-1 font-medium capitalize" style={{ background: `${meta.color}18`, color: meta.color }}>{meta.label}</span>
                        {e.event_type && <span className="rounded-full px-2.5 py-1 font-medium" style={{ background: `${e.event_type.color}18`, color: e.event_type.color }}>{e.event_type.name}</span>}
                        <span className="rounded-full border border-slate-700 px-2.5 py-1 capitalize text-slate-400">{e.status}</span>
                        <span className="ml-auto flex items-center gap-1 font-medium text-slate-300" data-testid={`member-event-participants-${e.id}`}><Users2 className="h-3.5 w-3.5 text-purple-400" /> {e.participant_count} participants</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="staff" className="mt-6">
            {!staff ? (
              <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl bg-slate-800/50" />)}</div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {staff.map((s, i) => (
                  <motion.div key={s.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="card-glass flex flex-col items-center rounded-2xl p-5 text-center transition-all hover:-translate-y-1 hover:shadow-[0_0_24px_rgba(139,92,246,0.2)]" data-testid={`member-staff-${s.id}`}>
                    <Avatar className="h-20 w-20 border-2 border-purple-500/40">
                      <AvatarImage src={s.avatar} />
                      <AvatarFallback className="bg-purple-600/30 text-lg text-purple-200">{`${s.first_name?.[0] || ""}${s.last_name?.[0] || ""}`}</AvatarFallback>
                    </Avatar>
                    <p className="mt-3 font-semibold text-white">{s.first_name} {s.last_name}</p>
                    {s.role && <span className="mt-1 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: `${s.role.color}18`, color: s.role.color }}><Shield className="h-3 w-3" /> {s.role.name}</span>}
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
